import React, { useMemo, useRef, useState } from "react";

import { Grid } from "@mui/material";
import { Stack, Typography } from "@mui/material";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import {
  FiDownload as DownloadIcon,
  FiArrowDownCircle as ArrowDownIcon,
  FiArrowUpCircle as ArrowUpIcon,
} from "react-icons/fi";

import { Booking } from "../../../../models/booking";
import { handleGetConvertValue } from "../../../../util/converts";
import exportAsImage from "../../../../util/exportImage";
import { DecimalFormatType } from "../../../../util/formatDecimalValue";
import { getMeasurement, getWeightMeasurement } from "../../../../util/getMeasurement";
import Button, { Color } from "../../../Form/Button";
import SkeletonComponent from "../../../Generic/Skeleton";
import { getScreenshotFilename, percentageChange } from "./actions";
import useStyles from "./styles";
import { CompareMode, defaultSummaryValues, SummaryValues, UnitOfMeasurement } from "./types";

const ICON_SIZE = 25;

interface SummaryProps {
  booking: Booking | undefined;
  loadingData?: boolean;
  viewOnly?: boolean;
}

export const BookingSummary: React.FC<SummaryProps> = (props) => {
  const { booking, loadingData, viewOnly } = props;
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const mainRef = useRef<HTMLDivElement>(null);

  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const [clickedToDownloadReport, setClickedToDownloadReport] = useState<boolean>(false);

  const summaryValues: SummaryValues = useMemo(() => {
    if (!booking) return defaultSummaryValues;
    const { cartons } = booking;

    return {
      cbm: {
        booked: booking?.bookedMeasurement || 0,
        received:
          cartons?.reduce((prev, curr) => {
            if (curr.isReceivedByCfs) return prev + (curr?.measurement ?? 0);

            return prev;
          }, 0) || 0,
      },
      weight: {
        booked: booking?.bookedGrossWeight || 0,
        received:
          cartons?.reduce((prev, curr) => {
            if (curr.isReceivedByCfs) return prev + (curr?.grossWeight ?? 0);

            return prev;
          }, 0) || 0,
      },
      quantity: {
        booked: cartons?.length ?? 0,
        received: Number(
          cartons?.reduce((prev, curr) => prev + (curr?.isReceivedByCfs ? 1 : 0), 0).toFixed(2)
        ),
      },
    };
  }, [booking?.cartons]);

  const onTakeScreenshot = () => {
    setClickedToDownloadReport(true);
    setTimeout(() => {
      if (mainRef && mainRef.current && booking?.containerId) {
        exportAsImage(mainRef.current, getScreenshotFilename(booking.containerId));
      }
      setClickedToDownloadReport(false);
    }, 100);
  };

  const downloadReportButton = () => {
    if (clickedToDownloadReport) return null;

    return (
      <Button
        startIcon={<DownloadIcon color="#fff" />}
        customColor={Color.SECONDARY}
        onClick={onTakeScreenshot}
      >
        {translate("downloadReport")}
      </Button>
    );
  };

  const summaryComparationItem = (label: string, value: string) => (
    <Grid item xs={6}>
      <Typography fontFamily="Manrope" fontWeight={500} fontSize={16}>
        {label}
      </Typography>
      <Typography fontFamily="Manrope" fontWeight={700} fontSize={24}>
        {value}
      </Typography>
    </Grid>
  );

  const lessThanBookedMessage = (message: string) => (
    <>
      <ArrowDownIcon size={ICON_SIZE} color="#F97066" />
      <Typography
        fontFamily="Manrope"
        fontWeight={600}
        fontSize={16}
        color="#F97066"
        paddingLeft={1}
      >
        {message}
      </Typography>
    </>
  );

  const moreThanBookedMessage = (message: string) => (
    <>
      <ArrowUpIcon size={ICON_SIZE} color="#039855" />
      <Typography
        fontFamily="Manrope"
        fontWeight={600}
        fontSize={16}
        color="#039855"
        paddingLeft={1}
      >
        {message}
      </Typography>
    </>
  );

  const summaryComparationMessage = (
    bookedValue: number,
    receivedValue: number,
    compareMode: CompareMode,
    unit: string,
    pluralUnit: string
  ) => {
    if (bookedValue === receivedValue) {
      return (
        <Typography fontFamily="Manrope" fontWeight={600} fontSize={16} color="#039855">
          {translate("theValuesWereTheSame")}
        </Typography>
      );
    }
    switch (compareMode) {
      case CompareMode.ABSOLUTE: {
        const absoluteValue = Math.abs(bookedValue - receivedValue);
        const absoluteValueUnit = absoluteValue > 1 ? pluralUnit : unit;

        if (bookedValue < receivedValue) {
          return moreThanBookedMessage(
            `${absoluteValue} ${absoluteValueUnit}  ${translate("moreThanBooked")}`
          );
        }

        return lessThanBookedMessage(
          `${absoluteValue} ${absoluteValueUnit}  ${translate("lessThanBooked")}`
        );
      }

      default: {
        const percentualValue = percentageChange(bookedValue, receivedValue);

        if (bookedValue < receivedValue) {
          return moreThanBookedMessage(`${percentualValue}% ${translate("moreThanBooked")}`);
        }

        return lessThanBookedMessage(`${percentualValue}% ${translate("lessThanBooked")}`);
      }
    }
  };

  const summaryComparationBox = (
    bookedValue: number,
    receivedValue: number,
    bookedLabel: string,
    receivedLabel: string,
    unit: string,
    pluralUnit: string,
    compareMode: CompareMode,
    decimals?: number
  ) => {
    const bookedUnit = bookedValue > 1 ? pluralUnit : unit;
    const receivedUnit = receivedValue > 1 ? pluralUnit : unit;

    return (
      <Grid item xs={12} sm={6} md={viewOnly ? 6 : 4} spacing={2}>
        <div className={classes.summaryComparationBox}>
          <Grid container>
            {summaryComparationItem(
              bookedLabel,
              `${decimals ? bookedValue.toFixed(decimals) : bookedValue} ${bookedUnit}`
            )}
            {summaryComparationItem(
              receivedLabel,
              `${decimals ? receivedValue.toFixed(decimals) : receivedValue} ${receivedUnit}`
            )}
            <Grid item xs={12} className={classes.summaryComparationLabel}>
              <div className={classes.summaryComparationMessageContainer}>
                {summaryComparationMessage(
                  Number(bookedValue.toFixed(decimals)),
                  Number(receivedValue.toFixed(decimals)),
                  compareMode,
                  unit,
                  pluralUnit
                )}
              </div>
            </Grid>
          </Grid>
        </div>
      </Grid>
    );
  };

  return (
    <div ref={mainRef} data-cy="summary-box" className={classNames(classes.mainDiv)}>
      <SkeletonComponent isLoading={!!loadingData}>
        <>
          {!viewOnly ? (
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              paddingBottom={2}
            >
              <div className={classes.title}>{translate("bookingSummary")}</div>
              {downloadReportButton()}
            </Stack>
          ) : null}

          <Grid container direction="row" spacing={2}>
            {summaryComparationBox(
              Number(
                handleGetConvertValue(
                  summaryValues.cbm.booked,
                  measurement?.code,
                  DecimalFormatType.MAERSK
                )
              ),
              Number(
                handleGetConvertValue(
                  summaryValues.cbm.received,
                  measurement?.code,
                  DecimalFormatType.MAERSK
                )
              ),
              `${translate("bookedVolume")}`, // mudar os nomes
              `${translate("receivedVolume")}`,
              measurement?.code || UnitOfMeasurement.CBM,
              measurement?.code || UnitOfMeasurement.CBM,
              CompareMode.PERCENTUAL,
              DecimalFormatType.MAERSK
            )}
            {summaryComparationBox(
              Number(
                handleGetConvertValue(
                  summaryValues.weight.booked,
                  measurementWeight?.code,
                  DecimalFormatType.MAERSK
                )
              ),
              Number(
                handleGetConvertValue(
                  summaryValues.weight.received,
                  measurementWeight?.code,
                  DecimalFormatType.MAERSK
                )
              ),
              `${translate("bookedWeight")}`,
              `${translate("receivedWeight")}`,
              measurementWeight?.code || UnitOfMeasurement.WEIGHT,
              measurementWeight?.code || UnitOfMeasurement.WEIGHT,
              CompareMode.PERCENTUAL,
              DecimalFormatType.MAERSK
            )}
            {summaryComparationBox(
              summaryValues.quantity.booked,
              summaryValues.quantity.received,
              translate("ssccQuantityBooked"),
              translate("ssccQuantityReceived"),
              UnitOfMeasurement.SSCC,
              UnitOfMeasurement.SSCC_PLURAL,
              CompareMode.ABSOLUTE
            )}
          </Grid>
        </>
      </SkeletonComponent>
    </div>
  );
};
